import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { List, ListItem } from '@fluentui/react-list-preview';

import { Card, mergeClasses, Title2, Caption2, Caption1Strong, Body2, ToggleButton } from '@fluentui/react-components';
import { Overlay } from '@bingads-webui-react/fabric-control';
import { AdsStudioSpinner } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';

import { useTemplateVideoStyles, useTemplateGridStyles } from './style';

export const VideoTemplateCard = ({
  template,
  setClickedTemplate,
  i18n,
  getAssetCountInfo,
}) => {
  const classes = useTemplateVideoStyles();

  const onClick = () => {
    setClickedTemplate(template);
  };

  return (
    <div className={mergeClasses(classes.flexColumn, classes.templateWithCaption)}>
      <ListItem onAction={onClick} aria-label={template.LocalizedTemplateName || template.TemplateName}>
        <Card className={classes.videoCard}>
          <img src={template.TemplateThumbnailUrl} alt={i18n.getString(_TL_('Preview of video ad template'))} />
        </Card>
      </ListItem>
      <div className={mergeClasses(classes.flexColumn, classes.caption)}>
        <Caption1Strong className={classes.darkGrayText}> {template.LocalizedTemplateName || template.TemplateName} </Caption1Strong>
        <Caption2 className={classes.grayText}> {getAssetCountInfo(template)} </Caption2>
      </div>
    </div>
  );
};

VideoTemplateCard.propTypes = {
  template: PropTypes.shape({
    TemplateId: PropTypes.string.isRequired,
    TemplateName: PropTypes.string.isRequired,
    Duration: PropTypes.number,
    AspectRatio: PropTypes.string,
    Themes: PropTypes.arrayOf(PropTypes.string),
    NumberOfImages: PropTypes.number,
    NumberOfText: PropTypes.number,
    TemplateThumbnailUrl: PropTypes.string.isRequired,
    TemplatePreviewUrl: PropTypes.string.isRequired,
    // NumberOfLogos: PropTypes.string.isRequired,
  }).isRequired,
  setClickedTemplate: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  getAssetCountInfo: PropTypes.func.isRequired,
};


export const TemplateGrid = ({
  templates,
  setClickedTemplate,
  loading,
  i18n,
  logger,
  getAssetCountInfo,
  templateTagOptions,
  templateTagsMapping,
}) => {
  const classes = useTemplateGridStyles();
  const [selectedFilter, setSelectedFilter] = useState(templateTagOptions.all);
  const filterOptions = [templateTagOptions.all, templateTagOptions.brandAwareness, templateTagOptions.performance, templateTagOptions.premiumStreaming, templateTagOptions.onlineVideo];

  const getTitleMessage = useMemo(() => (
    (templates && templates.length) ? i18n.getString(_TL_('Video templates')) : i18n.getString(_TL_('No templates found'))
  ), [templates, i18n]);

  return (
    <>
      {loading ? (
        <div aria-busy="true" aria-label={i18n.getString(_TL_('Loading templates'))}>
          <Overlay>
            <AdsStudioSpinner className="page-load-spinner" useCustomSize customStyle={{ margin: 250 }} />
          </Overlay>
        </div>
      ) : (
        <div>
          <Title2 block>{ getTitleMessage }</Title2>
          {
            (templates && templates.length > 0) &&
            <div>
              <div className={classes.templateBody}>
                <Body2 className="video-templates-banner-description">
                  {i18n.getString(_TL_('Create a video using a template.'))}
                </Body2>
              </div>
              <div className={mergeClasses('template-filter', classes.filtersRow)}>
                {filterOptions.map((filter, index) => (
                  <React.Fragment key={filter}>
                    <ToggleButton
                      onClick={() => setSelectedFilter(filter)}
                      className={mergeClasses(`filter-${index}`, classes.filterButton)}
                      disabled={loading}
                      checked={selectedFilter === filter}
                    >
                      {i18n.getString(filter)}
                    </ToggleButton>
                  </React.Fragment>
                ))}
              </div>
              <List
                navigationMode="items"
                className={mergeClasses('template-grid', classes.gridContainer)}
              >
                {templates.filter((template) => {
                    const { TemplateId } = template;
                    const templateDetails = templateTagsMapping[TemplateId];
                    return templateDetails && (selectedFilter === templateTagOptions.all || (templateDetails && templateDetails.tags && templateDetails.tags.includes(selectedFilter)));
                  }).map((template) => {
                    const { TemplateId } = template;
                    return (
                      <VideoTemplateCard
                        key={TemplateId}
                        template={template}
                        setClickedTemplate={setClickedTemplate}
                        i18n={i18n}
                        logger={logger}
                        getAssetCountInfo={getAssetCountInfo}
                      />
                    );
                  })}
              </List>
            </div>
          }
        </div>
      )}
    </>
  );
};

TemplateGrid.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  templates: PropTypes.arrayOf(PropTypes.shape({
    TemplateId: PropTypes.string.isRequired,
    TemplateName: PropTypes.string.isRequired,
    Duration: PropTypes.number,
    AspectRatio: PropTypes.string,
    Themes: PropTypes.arrayOf(PropTypes.string),
    NumberOfImages: PropTypes.number,
    NumberOfText: PropTypes.number,
    TemplateThumbnailUrl: PropTypes.string.isRequired,
    TemplatePreviewUrl: PropTypes.string.isRequired,
    // NumberOfLogos: PropTypes.string.isRequired,
  })).isRequired,
  setClickedTemplate: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
  getAssetCountInfo: PropTypes.func.isRequired,
};

TemplateGrid.defaultProps = {
  loading: true,
};
